import classNames from 'classnames';
import {
  differenceInBusinessDays,
  differenceInCalendarMonths,
  isSameDay,
} from 'date-fns';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Skeleton } from 'atoms/animations/Skeleton';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LabelL, LabelS, LabelXS } from 'atoms/typography';
import { Ellipsis } from 'atoms/typography/Ellipsis';
import { Color } from 'style/types';

const DayLabel = styled(LabelS)`
  text-transform: uppercase;
`;

const DateLabel = styled(LabelL)`
  white-space: nowrap;
`;

const Tag = styled(Horizontal).attrs({ gap: 0.5, center: true })`
  border-radius: 3px 3px 10px 10px;
  margin-bottom: var(--half-unit);
  padding: 0 var(--half-unit);
  width: 100%;
`;

const TodayLabel = styled(LabelS)`
  text-transform: uppercase;
`;

const TodayTag = styled(Tag)`
  background-color: var(--c-red-600);
`;

const wrapperStyles = css`
  display: flex;
  min-width: 65px;
  max-width: 65px;
  aspect-ratio: 1;
  border-radius: var(--double-unit);
`;
const Wrapper = styled.button`
  ${wrapperStyles}
  flex-direction: column;
  gap: var(--half-unit);
  justify-content: center;
  align-items: center;
  padding: 0 var(--half-unit);
  background: var(--c-neutral-300);
  cursor: pointer;
  &.selected {
    background: var(--c-neutral-1000);
  }
  &.withTag {
    justify-content: flex-end;
  }
  &.disabled {
    background: var(--c-neutral-200);
    color: var(--c-neutral-400);
  }
`;

const StyledSkeleton = styled(Skeleton)`
  ${wrapperStyles}
`;

type Props =
  | {
      date: Date;
      selected?: boolean;
      onClick?: () => void;
      disabled?: boolean;
    }
  | {
      loading: true;
    };
export const DatePickerItem = (props: Props) => {
  if ('loading' in props) {
    return <StyledSkeleton />;
  }
  const { date, selected, onClick, disabled } = props;
  const dayLabelColor = disabled
    ? 'var(--c-neutral-400)'
    : 'var(--c-neutral-500)';

  let dateLabelColor: Color = 'var(--c-neutral-1000)';
  if (differenceInBusinessDays(date, new Date()) < 0) {
    dateLabelColor = 'var(--c-neutral-500)';
  }
  if (selected) {
    dateLabelColor = 'var(--c-neutral-100)';
  }
  if (disabled) {
    dateLabelColor = 'var(--c-neutral-400)';
  }

  return (
    <Wrapper
      className={classNames({
        selected,
        disabled,
        withTag:
          isSameDay(date, new Date()) ||
          differenceInCalendarMonths(new Date(), date) >= 11,
      })}
      role="button"
      onClick={onClick}
      disabled={disabled}
    >
      <Vertical gap={0} center>
        <DayLabel as="p" bold color={dayLabelColor}>
          <FormattedDate value={date} weekday="short" />
        </DayLabel>

        <DateLabel as="p" bold color={dateLabelColor}>
          <FormattedDate value={date} day="numeric" month="numeric" />
        </DateLabel>
      </Vertical>
      {isSameDay(date, new Date()) && (
        <TodayTag>
          <TodayLabel as="p" bold color="var(--c-neutral-1000)">
            <Ellipsis>
              <FormattedMessage
                id="DatePickerItem.todayMax5Letters"
                defaultMessage="Today"
              />
            </Ellipsis>
          </TodayLabel>
        </TodayTag>
      )}
      {differenceInCalendarMonths(new Date(), date) >= 11 && (
        <LabelXS bold as="p" color={dayLabelColor}>
          {date.getFullYear()}
        </LabelXS>
      )}
    </Wrapper>
  );
};
